import axios from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { listBarriers } from '@/api/endpoints/barriers';
import { useTileRevision } from '@/stores/TileRevisionContext';
import type {
  BarrierListFilters,
  BarrierListItem,
  BarrierListSort,
} from '@/types/barriers';
import { extractAxiosMessage } from '@/utils/axiosError';

const DEFAULT_PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

export const BARRIERS_LIBRARY_PAGE_SIZE = DEFAULT_PAGE_SIZE;

export const EMPTY_BARRIER_FILTERS: BarrierListFilters = {
  periodFrom: null,
  periodTo: null,
  radiusMinM: null,
  radiusMaxM: null,
  direction: null,
  hasWaypoints: null,
  search: '',
};

/** Recently created (or edited) barriers surface at the top by default. */
export const DEFAULT_BARRIER_SORT: BarrierListSort = {
  field: 'updated_at',
  direction: 'desc',
};

export type BarriersLibraryStatus = 'idle' | 'loading' | 'success' | 'error';

export interface UseBarriersLibraryValue {
  readonly items: readonly BarrierListItem[];
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
  readonly status: BarriersLibraryStatus;
  readonly errorMessage: string | null;
  readonly filters: BarrierListFilters;
  /** Filters actually applied to the current result set (search debounced).
   * Use these — not `filters` — for "select all across pages" so the
   * selected set matches what the table is showing. */
  readonly appliedFilters: BarrierListFilters;
  readonly sort: BarrierListSort;
  readonly setFilters: (next: BarrierListFilters) => void;
  readonly resetFilters: () => void;
  readonly setSort: (next: BarrierListSort) => void;
  readonly setPage: (page: number) => void;
  readonly refresh: () => void;
}

export function useBarriersLibrary(): UseBarriersLibraryValue {
  const [filters, setFiltersState] = useState<BarrierListFilters>(EMPTY_BARRIER_FILTERS);
  const [sort, setSortState] = useState<BarrierListSort>(DEFAULT_BARRIER_SORT);
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<readonly BarrierListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState<BarriersLibraryStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [reloadCounter, setReloadCounter] = useState(0);
  const { barriersRevision } = useTileRevision();
  const controllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearch(filters.search.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [filters.search]);

  const queryFilters = useMemo<BarrierListFilters>(
    () => ({ ...filters, search: debouncedSearch }),
    [filters, debouncedSearch],
  );

  // Reset to page 1 when any filter changes — otherwise a narrowing
  // search could land on an out-of-range offset and show "empty".
  useEffect(() => {
    setPage(1);
  }, [
    queryFilters.periodFrom,
    queryFilters.periodTo,
    queryFilters.radiusMinM,
    queryFilters.radiusMaxM,
    queryFilters.direction,
    queryFilters.hasWaypoints,
    queryFilters.search,
    sort.field,
    sort.direction,
  ]);

  useEffect(() => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;

    setStatus('loading');
    setErrorMessage(null);
    listBarriers({
      limit: DEFAULT_PAGE_SIZE,
      offset: (page - 1) * DEFAULT_PAGE_SIZE,
      periodFrom: queryFilters.periodFrom,
      periodTo: queryFilters.periodTo,
      radiusMinM: queryFilters.radiusMinM,
      radiusMaxM: queryFilters.radiusMaxM,
      direction: queryFilters.direction,
      hasWaypoints: queryFilters.hasWaypoints,
      search: queryFilters.search,
      sortBy: sort.field,
      sortDir: sort.direction,
      signal: controller.signal,
    })
      .then((response) => {
        if (controller.signal.aborted) return;
        setItems(response.items);
        setTotal(response.total);
        setStatus('success');
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted || axios.isCancel(error)) return;
        setStatus('error');
        setErrorMessage(extractAxiosMessage(error) ?? 'unknown error');
      });

    return () => controller.abort();
  }, [page, queryFilters, sort, barriersRevision, reloadCounter]);

  const setFilters = useCallback((next: BarrierListFilters) => {
    setFiltersState(next);
  }, []);

  const resetFilters = useCallback(() => {
    setFiltersState(EMPTY_BARRIER_FILTERS);
  }, []);

  const setSort = useCallback((next: BarrierListSort) => {
    setSortState(next);
  }, []);

  const refresh = useCallback(() => {
    setReloadCounter((value) => value + 1);
  }, []);

  return {
    items,
    total,
    page,
    pageSize: DEFAULT_PAGE_SIZE,
    status,
    errorMessage,
    filters,
    appliedFilters: queryFilters,
    sort,
    setFilters,
    resetFilters,
    setSort,
    setPage,
    refresh,
  };
}
